import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Dumbbell, Target, Users, TrendingUp } from 'lucide-react';
import { siteData } from '../data/mock';

const Coaching = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const programs = [
    {
      title: "Perte de poids",
      description: "Programme personnalisé pour perdre du poids durablement, sans effet yoyo.",
      icon: TrendingUp,
      features: [
        "Bilan complet de départ",
        "Programme d'entraînement adapté",
        "Suivi nutritionnel inclus",
        "Objectifs réalistes et mesurables"
      ]
    },
    {
      title: "Prise de masse musculaire",
      description: "Développe ta masse musculaire avec un programme structuré et efficace.",
      icon: Dumbbell,
      features: [
        "Entraînement en force",
        "Plan alimentaire protéiné",
        "Progression suivie",
        "Techniques d'entraînement avancées"
      ]
    },
    {
      title: "Remise en forme",
      description: "Retrouve la forme et l'énergie au quotidien avec un coaching motivant.",
      icon: Target,
      features: [
        "Programme progressif",
        "Exercices variés",
        "Amélioration cardio",
        "Renforcement global"
      ]
    },
    {
      title: "Coaching personnalisé",
      description: "Un accompagnement 100% sur-mesure adapté à tes besoins spécifiques.",
      icon: Users,
      features: [
        "Séances individuelles",
        "Écoute et ajustements",
        "Horaires flexibles",
        "En salle ou à domicile"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pt-20">
      {/* Hero */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animated">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Coaching Sportif Personnalisé
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Un accompagnement sur-mesure pour atteindre tes objectifs, quel que soit ton niveau de départ.
              </p>
              <Link to="/contact" className="btn-primary">
                Réserver ma première séance
              </Link>
            </div>
            <div className="animated">
              <img
                src={siteData.images.coaching1}
                alt="Coaching personnalisé"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Programmes disponibles
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-2xl mx-auto">
              Chaque programme est adapté à ton niveau et à tes objectifs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {programs.map((program, index) => {
              const Icon = program.icon;
              return (
                <div
                  key={index}
                  className="network-card animated"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 rounded-full bg-[var(--brand-primary)] flex items-center justify-center mb-4">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-3">
                    {program.title}
                  </h3>
                  <p className="text-[var(--text-secondary)] mb-4">
                    {program.description}
                  </p>
                  <ul className="space-y-2">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-[var(--text-secondary)]">
                        <CheckCircle size={18} className="text-[var(--brand-primary)] flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Comment ça marche ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Premier contact",
                description: "On échange sur tes objectifs, ton historique sportif et tes disponibilités."
              },
              {
                step: "2",
                title: "Bilan complet",
                description: "Première séance pour évaluer ton niveau et créer ton programme personnalisé."
              },
              {
                step: "3",
                title: "Accompagnement",
                description: "Suivi régulier, ajustements et motivation pour des résultats durables."
              }
            ].map((item, index) => (
              <div
                key={index}
                className="text-center animated"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 rounded-full bg-[var(--brand-primary)] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-3">
                  {item.title}
                </h3>
                <p className="text-[var(--text-secondary)]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animated">
              <img
                src={siteData.images.coaching2}
                alt="Séance de coaching"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="animated">
              <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6">
                Un coach à ton écoute
              </h2>
              <p className="text-xl text-[var(--text-secondary)] mb-6">
                Chaque personne est unique. C'est pourquoi je prends le temps de comprendre tes besoins, tes contraintes et tes objectifs.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Séances 100% personnalisées",
                  "Adaptation à ton rythme",
                  "Motivation et bienveillance",
                  "Résultats mesurables et durables"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle className="text-[var(--brand-primary)] flex-shrink-0" size={24} />
                    <span className="text-lg text-[var(--text-secondary)]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-primary">
                Commencer maintenant
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="max-w-3xl mx-auto animated">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à te lancer ?
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              La première séance est un bilan gratuit pour faire connaissance et définir ton programme
            </p>
            <Link to="/contact" className="btn-primary">
              Réserver ma première séance
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Coaching;
